import React, { useEffect, useRef } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Zap,
  CheckCircle,
  IndianRupee,
  Download,
  FileText,
} from "lucide-react";
import { Button } from "./ui/button";
import { trackCTAClick } from "../utils/analytics";
import { trackCAPIViewContent } from "../utils/metaConversionsAPI";

export const ProgrammeSection = ({ onApplyClick }) => {
  const sectionRef = useRef(null);

  // Track Meta Pixel ViewContent event when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Generate unique event ID for deduplication
            const viewEventId = `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Track Meta Pixel ViewContent event (browser-side)
            if (
              typeof window !== "undefined" &&
              typeof window.fbq === "function"
            ) {
              window.fbq("track", "ViewContent", {
                content_name: "Programmes Section",
                content_category: "Acting Programmes",
                content_ids: ["Diploma", "Weekend"],
              }, { eventID: viewEventId });
            }
            // Track Meta Conversions API ViewContent event (server-side) with same event ID
            trackCAPIViewContent("Programmes Section", ["Diploma", "Weekend"], viewEventId);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="programmes"
      className="py-20 px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Learning Path
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Both programmes are highly intensive, practical, and advanced —
            personally designed and conducted by{" "}
            <span className="font-bold text-purple-600">
              Barry John himself
            </span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Diploma Programme */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-purple-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
            <div className="relative bg-white rounded-3xl p-8 border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 shadow-lg hover:shadow-2xl">
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 text-sm font-bold rounded-full mb-6">
                FULL-TIME PROGRAMME
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                Diploma in Film & Theatre Acting
              </h3>
              <p className="text-gray-600 mb-6">
                A full-fledged Acting Course for those who want a career in
                Acting. It is an intensive, progresive journey for serious
                aspiring actors.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Starts:</span> 16 November 2026
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Ends:</span> 05 February 2027
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Duration:</span> 3 months
                    (Full-time)
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Zap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Schedule:</span> Mon-Fri,
                    8am-4pm
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Batch Size:</span> 20
                    students
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-sm">
                    <span className="font-semibold">Location:</span> First
                    floor, Global Chambers, 101, near Infinity Mall, Veera Desai
                    Industrial Estate, Andheri West, Mumbai, Maharashtra 400053
                  </span>
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Personal mentorship from Barry John
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Diploma certificate on completion
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Intensive practical training with real-world application
                    </span>
                  </div>
                </div>
              </div>

              {/* Fee Information */}
              <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <IndianRupee className="w-5 h-5 text-purple-600" />
                  <span className="font-bold text-lg text-gray-900">
                    Programme Fee
                  </span>
                </div>
                <p className="text-2xl font-bold text-purple-600 mb-1">
                  ₹2,00,000 + GST (18%)
                </p>
                <p className="text-sm text-gray-600">Total: ₹2,36,000</p>
              </div>

              <Button
                onClick={() => {
                  trackCTAClick(
                    "programme_section",
                    "Apply for Diploma",
                    "diploma_card",
                  );
                  onApplyClick("application");
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white text-lg font-semibold py-6 shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 transition-all duration-300"
              >
                Apply for Diploma
              </Button>
            </div>
          </div>

          {/* Weekend Programme */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
            <div className="relative bg-white rounded-3xl p-8 border-2 border-indigo-200 hover:border-indigo-400 transition-all duration-300 shadow-lg hover:shadow-2xl">
              <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 text-sm font-bold rounded-full mb-6">
                PART-TIME PROGRAMME
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                Weekend Certificate Programme
              </h3>
              <p className="text-gray-600 mb-6">
                Designed for working professionals, students, and beginners who
                want to explore acting.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Starts:</span> 14 November 2026
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Calendar className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Ends:</span> 31 January 2027
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Duration:</span> 3 months
                    (Weekends)
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Zap className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Schedule:</span> Sat-Sun,
                    9am-1:30pm
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Batch Size:</span> 20
                    students
                  </span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm">
                    <span className="font-semibold">Location:</span> First
                    floor, Global Chambers, 101, near Infinity Mall, Veera Desai
                    Industrial Estate, Andheri West, Mumbai, Maharashtra 400053
                  </span>
                </div>
              </div>

              <div className="bg-indigo-50 rounded-xl p-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Personal mentorship from Barry John
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Certificate on completion
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      Weekend schedule for flexible learning
                    </span>
                  </div>
                </div>
              </div>

              {/* Fee Information */}
              <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-5 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <IndianRupee className="w-5 h-5 text-indigo-600" />
                  <span className="font-bold text-lg text-gray-900">
                    Programme Fee
                  </span>
                </div>
                <p className="text-2xl font-bold text-indigo-600 mb-1">
                  ₹70,000 + GST (18%)
                </p>
                <p className="text-sm text-gray-600">Total: ₹82,600</p>
              </div>

              <Button
                onClick={() => {
                  trackCTAClick(
                    "programme_section",
                    "Apply for Weekend",
                    "weekend_card",
                  );
                  onApplyClick("application");
                }}
                className="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white text-lg font-semibold py-6 shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 transition-all duration-300"
              >
                Apply for Weekend
              </Button>
            </div>
          </div>
        </div>

        {/* Course Details Download CTA */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 lg:p-10 border-2 border-purple-200 hover:border-purple-300 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FileText className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                  Download Complete Course Details
                </h3>
                <p className="text-gray-600 text-lg">
                  Get comprehensive information about our programmes,
                  curriculum, schedule, and more
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={() => {
                    trackCTAClick(
                      "programme_section",
                      "Download PDF",
                      "course_details_cta",
                    );
                    onApplyClick("download");
                  }}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <Download className="w-5 h-5" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
